import { EyeOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { Space, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { IFSTableRow, Pagination } from '@app/models/import_free_sales/ImpFreeSalesResponse';
import { getImpFreeSalesData } from '@app/api/prImportFreeSales.api';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export const ProductRegistrationTable: React.FC = () => {
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[] | null>(null);
  const [productOptions, setProductOptions] = useState<{ label: string; value: string }[] | null>(null);
  const [prtypeOptions, setPRTypeOptions] = useState<{ label: string; value: string }[] | null>(null);
  const lastPathnameRef = useRef<string | null>(null);
  const [tableData, setTableData] = useState<{ data: IFSTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const catType = localStorage.getItem('selected_category_type') || 'SINGLE';

  const fetch = useCallback(
    (pagination = { current: 1, pageSize: 10 }, filterParams = {}) => {
      setTableData((prev) => ({ ...prev, loading: true }));

      const storedCategoryId = Number(localStorage.getItem('selected_category_id'));

      const effectiveCategoryId = storedCategoryId && storedCategoryId !== 0 ? storedCategoryId : 2;

      const params: {
        [key: string]: any;
        is_location: boolean | null;
      } = {
        ...pagination,
        pr_category_id: effectiveCategoryId,
        ...filterParams,
        is_location: null,
      };

      if (catType === 'LOCATION') {
        params.is_location = true;
      }

      getImpFreeSalesData(params)
        .then((res) => {
          setTableData({
            data: res.data || [],
            pagination: res.pagination,
            loading: false,
          });
          setStatusOptions(res.status_options);
          setProductOptions(res.pr_product_options);
          setPRTypeOptions(res.pr_types_options);
        })
        .catch((error) => {
          notificationController.error({ message: error.message });
          setTableData((prev) => ({ ...prev, loading: false }));
        });
    },
    [catType],
  );

  useEffect(() => {
    if (lastPathnameRef.current !== location.pathname) {
      lastPathnameRef.current = location.pathname;
      fetch();
    }
  }, [location.pathname]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch(
      {
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 5,
      },
      filters,
    );
  };

  const handleFilterChange = (newFilters: { [key: string]: string | [string, string] | null }) => {
    setFilters(newFilters);

    fetch(
      {
        current: 1,
        pageSize: tableData.pagination.pageSize || 10,
      },
      newFilters,
    );
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-product-registrations`, { state: { id } });
  };

  const handleEditRow = (id: number) => {
    navigate(`/edit-product-registrations`, { state: { id } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Registrations`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'code', placeholder: 'Code', type: 'text' as const },
    ...(catType === 'LOCATION'
      ? [{ name: 'location', placeholder: 'Location/Location Code', type: 'text' as const }]
      : []),
    {
      name: 'pr_product_id',
      placeholder: 'Product',
      type: 'dropdown' as const,
      options: productOptions ? { productOptions: productOptions } : {},
    },
    {
      name: 'pr_type',
      placeholder: 'Product Type',
      type: 'dropdown' as const,
      options: prtypeOptions ? { prtypeOptions: prtypeOptions } : {},
    },
    {
      name: 'status',
      placeholder: 'Status',
      type: 'dropdown' as const,
      options: statusOptions ? { statusOptions: statusOptions } : {},
    },
  ];

  const columns: ColumnsType<IFSTableRow> = [
    {
      title: 'PR Code',
      dataIndex: 'code',
      key: 'code',
      width: '10%',
    },
    ...(catType === 'LOCATION'
      ? [
          {
            title: 'Location Code',
            dataIndex: 'pr_location_code',
            key: 'pr_location_code',
            width: '10%',
          },
          {
            title: 'Location',
            dataIndex: 'pr_location_name',
            key: 'pr_location_name',
            width: '10%',
          },
        ]
      : []),
    {
      title: 'Product Category',
      dataIndex: 'pr_product_id',
      key: 'pr_product_id',
      render: (pr_product_id) => {
        const product = productOptions?.find((p) => p.value === pr_product_id);
        return product ? product.label : 'Unknown';
      },
      width: '20%',
    },

    {
      title: 'PR Type',
      dataIndex: 'pr_type',
      key: 'pr_type',
      width: '10%',
    },
    {
      title: 'Valid Upto',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (expires_at) => {
        if (!expires_at) return '-';
        const date = new Date(expires_at);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '20%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            {/* <Tooltip title="Edit">
              <Button
                type="text"
                onClick={() => handleEditRow(record.id)}
                icon={<EditOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>
 */}
          </Space>
        );
      },
    },
  ];
  return (
    <BaseCard id="basic-table" title="Product Registrations" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={handleFilterChange} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
